import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import MaintenancePage from './MaintenancePage';
import GlobalSearch from '../components/GlobalSearch';
import { ShareProfileButton } from '../components/ShareProfileButton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Store, MapPin, Clock, Instagram, Facebook, Globe, Heart, MessageSquare,
  Share2, Calendar, Users, Euro, Trophy, Loader2, ArrowRight, Check, Dices, MessageCircle, Mail, Phone,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const absUrl = (u) => (!u ? null : (u.startsWith('http') || u.startsWith('data:') ? u : `${API_URL}${u}`));

const GAME_COLORS = { magic: '#F59E0B', pokemon: '#EF4444', yugioh: '#7B68EE', lorcana: '#4ECDC4', 'flesh-and-blood': '#EC4899', 'one-piece': '#3B82F6' };
const gameColor = (g) => GAME_COLORS[(g || '').toLowerCase()] || '#FF6B35';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';

const DAYS = [['monday', 'Lundi'], ['tuesday', 'Mardi'], ['wednesday', 'Mercredi'], ['thursday', 'Jeudi'], ['friday', 'Vendredi'], ['saturday', 'Samedi'], ['sunday', 'Dimanche']];

const TournamentCard = ({ t, index }) => {
  const gc = gameColor(t.game);
  const remaining = t.capacity > 0 ? Math.max(0, t.capacity - (t.participants_count || 0)) : null;
  return (
    <Link
      to={`/player/event/tournament/${t.id}`}
      className="group block bg-[#1a1a1a] border border-[#333] rounded-xl p-5 hover:border-[#FF6B35]/50 hover:-translate-y-0.5 transition-all duration-200 opacity-0 animate-[fadeInUp_0.4s_ease_forwards]"
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`shop-tournament-${t.id}`}
    >
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="sm:w-28 shrink-0 flex sm:flex-col items-center sm:items-start gap-2">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white" style={{ backgroundColor: `${gc}22`, color: gc }}>{t.game || 'TCG'}</span>
          <div className="text-[#B0B0B0] text-sm"><span className="text-white font-semibold">{fmtDate(t.date)}</span> · {fmtTime(t.date)}</div>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-base truncate group-hover:text-[#FF6B35] transition-colors">{t.name}</h3>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-[#666]">
            {t.format && <span>{t.format}</span>}
            <span className="inline-flex items-center gap-1"><Euro className="w-3.5 h-3.5" />{t.price > 0 ? `${t.price} €` : 'Gratuit'}</span>
            {t.capacity > 0 && <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" />{t.participants_count || 0}/{t.capacity}</span>}
          </div>
        </div>
        <div className="flex items-center sm:items-end">
          {remaining !== null && remaining <= Math.ceil((t.capacity || 0) * 0.2) && remaining > 0 && (
            <span className="hidden sm:inline text-[#FF6B35] text-xs font-medium mr-3">Plus que {remaining} places</span>
          )}
          <span className="inline-flex items-center gap-1.5 text-[#FF6B35] text-sm font-medium">Voir <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>
        </div>
      </div>
    </Link>
  );
};

export default function ShopProfilePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState('upcoming');
  const [tournaments, setTournaments] = useState([]);
  const [tLoading, setTLoading] = useState(false);
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const loadShop = useCallback(async () => {
    setLoading(true); setNotFound(false);
    try {
      const res = await api.get(`/public/shops/${slug}`);
      setShop(res.data);
      setFollowing(!!res.data.is_following);
    } catch (e) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const loadTournaments = useCallback(async (when) => {
    setTLoading(true);
    try {
      const res = await api.get(`/public/shops/${slug}/tournaments`, { params: { when } });
      setTournaments(res.data);
    } catch (e) {
      setTournaments([]);
    } finally {
      setTLoading(false);
    }
  }, [slug]);

  useEffect(() => { loadShop(); }, [loadShop]);
  useEffect(() => { if (shop) loadTournaments(tab); }, [shop, tab, loadTournaments]);

  const toggleFollow = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setBusy(true);
    try {
      if (following) { await api.delete(`/users/favorite-shops/${shop.id}`); setFollowing(false); toast.success('Boutique retirée de vos favoris'); }
      else { await api.post(`/users/favorite-shops/${shop.id}`); setFollowing(true); toast.success('Vous suivez cette boutique !'); }
    } catch (e) { toast.error('Une erreur est survenue'); }
    finally { setBusy(false); }
  };

  const contactOwner = () => {
    if (!shop.email && !shop.phone && !shop.discord && !shop.whatsapp) {
      toast.info("Cette boutique n'a pas renseigné de coordonnées de contact.");
      return;
    }
    setContactOpen(true);
  };

  const sharePage = async () => {
    const url = `${window.location.origin}/${shop.slug}`;
    try {
      if (navigator.share) { await navigator.share({ title: shop.name, url }); }
      else { await navigator.clipboard.writeText(url); toast.success('Lien copié dans le presse-papier !'); }
    } catch (e) { /* cancelled */ }
  };

  if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#FF6B35] animate-spin" /></div>;
  if (notFound) {
    if (!isAuthenticated) return <MaintenancePage />;
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center px-4 text-center">
        <Store className="w-14 h-14 text-[#333] mb-4" />
        <h1 className="text-white text-2xl font-bold mb-2">Boutique introuvable</h1>
        <p className="text-[#666] mb-6">La page{slug ? ` « ${slug} »` : ''} n'existe pas ou n'est plus disponible.</p>
        <Link to="/home" className="px-5 py-2.5 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white font-medium transition-colors">Retour à l'accueil</Link>
      </div>
    );
  }

  const cover = absUrl(shop.banner_url);
  const logo = absUrl(shop.logo_url);
  const fmtHours = (v) => {
    if (!v) return null;
    if (typeof v === 'string') return v;
    if (typeof v === 'object') {
      if (v.closed) return 'Fermé';
      const ranges = v.ranges || v.slots || v.hours;
      if (Array.isArray(ranges) && ranges.length) return ranges.map(r => `${r.open || r.from || r.start || ''}-${r.close || r.to || r.end || ''}`).join(', ');
      if (v.open && v.close) return `${v.open} - ${v.close}`;
      if (v.morning || v.afternoon) return [v.morning, v.afternoon].filter(Boolean).join(' · ');
      return null;
    }
    return String(v);
  };
  const openDays = DAYS.map(([k, label]) => [k, label, fmtHours(shop.opening_hours && shop.opening_hours[k])]).filter(([, , h]) => h);

  const isOpenNow = () => {
    try {
      const oh = shop.opening_hours;
      if (!oh) return null;
      const toMin = (t) => { const m = String(t).trim().match(/(\d{1,2})[:hH](\d{2})/); return m ? (+m[1]) * 60 + (+m[2]) : null; };
      const rangesFor = (v) => {
        if (!v) return [];
        if (typeof v === 'string') {
          if (/ferm/i.test(v)) return [];
          return v.split(',').map((p) => { const m = p.match(/(\d{1,2}[:hH]\d{2})\s*[-–à]\s*(\d{1,2}[:hH]\d{2})/); return m ? [toMin(m[1]), toMin(m[2])] : null; }).filter(Boolean);
        }
        if (typeof v === 'object') {
          if (v.closed) return [];
          const arr = v.ranges || v.slots || v.hours;
          if (Array.isArray(arr)) return arr.map((r) => [toMin(r.open || r.from || r.start), toMin(r.close || r.to || r.end)]).filter((x) => x[0] != null && x[1] != null);
          if (v.open && v.close) return [[toMin(v.open), toMin(v.close)]];
          const res = []; [v.morning, v.afternoon].forEach((s) => { if (s) { const m = String(s).match(/(\d{1,2}[:hH]\d{2})\s*[-–à]\s*(\d{1,2}[:hH]\d{2})/); if (m) res.push([toMin(m[1]), toMin(m[2])]); } }); return res;
        }
        return [];
      };
      const now = new Date();
      const dayKey = DAYS[(now.getDay() + 6) % 7][0];
      const ranges = rangesFor(oh[dayKey]);
      if (!ranges.length) return false;
      const cur = now.getHours() * 60 + now.getMinutes();
      return ranges.some(([a, b]) => a != null && b != null && cur >= a && cur <= b);
    } catch { return null; }
  };
  const openStatus = isOpenNow();

  return (
    <div className={isAuthenticated ? 'pb-10' : 'min-h-screen bg-[#0a0a0a] pb-20'}>
      {/* Public glass header (only for non-authenticated visitors; connected users keep the app nav) */}
      {!isAuthenticated && (
        <header className="fixed top-0 inset-x-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-[#333] h-16">
          <div className="max-w-6xl mx-auto h-full px-4 flex items-center gap-4">
            <Link to="/" className="shrink-0">
              <img src="/logo-complet-arcanapp.png" alt="Arcanapp" className="h-7" onError={(e) => { e.target.style.display = 'none'; }} />
            </Link>
            <div className="flex-1 max-w-md">
              <GlobalSearch />
            </div>
            <Link to="/login" className="shrink-0 px-4 py-1.5 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white text-sm font-semibold transition-colors">Se connecter</Link>
          </div>
        </header>
      )}

      <div className={isAuthenticated ? '' : 'pt-16'}>
        {/* Cover */}
        <div className="w-full h-40 sm:h-56 md:h-72 relative overflow-hidden bg-[#1a1a1a]" data-testid="shop-cover">
          {cover ? (
            <img src={cover} alt={`Couverture ${shop.name}`} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full" style={{ background: `linear-gradient(135deg, #1a1a1a, ${gameColor()}22)` }} />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        </div>

        {/* Header identity */}
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end gap-4 -mt-14 md:-mt-16 relative z-10">
            <div className="w-28 h-28 md:w-36 md:h-36 rounded-full border-4 border-[#0a0a0a] bg-[#1a1a1a] overflow-hidden shrink-0 shadow-xl" data-testid="shop-logo">
              {logo ? <img src={logo} alt={shop.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#FF6B35] text-4xl font-black">{(shop.name || '?').charAt(0).toUpperCase()}</div>}
            </div>
            <div className="flex-1 min-w-0 pb-1">
              <h1 className="text-white text-2xl md:text-3xl font-black tracking-tight truncate" data-testid="shop-name">{shop.name}</h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[#B0B0B0] text-sm">
                <span className="inline-flex items-center gap-1"><Store className="w-3.5 h-3.5 text-[#FF6B35]" />{shop.pro_type === 'association' ? 'Association' : shop.pro_type === 'bar' ? 'Bar à jeux' : 'Boutique de jeux'}</span>
                {shop.address && <span className="inline-flex items-center gap-1 truncate"><MapPin className="w-3.5 h-3.5" />{shop.address}</span>}
                {openStatus !== null && (
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${openStatus ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`} data-testid="shop-open-status">
                    <span className={`w-1.5 h-1.5 rounded-full ${openStatus ? 'bg-green-400' : 'bg-red-400'}`} />
                    {openStatus ? 'Ouvert' : 'Fermé'}
                  </span>
                )}
              </div>
            </div>
            {/* Actions */}
            <div className="flex items-center gap-2 pb-1">
              <button onClick={toggleFollow} disabled={busy} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-colors disabled:opacity-60 ${following ? 'bg-[#252525] text-white border border-[#333]' : 'bg-[#FF6B35] hover:bg-[#E55A25] text-white'}`} data-testid="shop-follow-btn">
                {following ? <><Check className="w-4 h-4" />Suivi</> : <><Heart className="w-4 h-4" />Suivre</>}
              </button>
              <button onClick={contactOwner} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm bg-[#252525] hover:bg-[#333] text-white border border-[#333] transition-colors disabled:opacity-60" data-testid="shop-contact-btn">
                <MessageSquare className="w-4 h-4" /><span className="hidden sm:inline">Contacter</span>
              </button>
              {isAuthenticated && shop.owner_id && user?.id !== shop.owner_id && (
                <button onClick={() => navigate(`/messages?to=${shop.owner_id}`)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm bg-[#4ECDC4]/15 hover:bg-[#4ECDC4]/25 text-[#4ECDC4] border border-[#4ECDC4]/30 transition-colors" data-testid="shop-message-btn">
                  <MessageCircle className="w-4 h-4" /><span className="hidden sm:inline">Message</span>
                </button>
              )}
              <ShareProfileButton
                url={`${window.location.origin}/${shop.slug}`}
                title={`Boutique ${shop.name}`}
                variant="outline"
                label="Partager"
                className="px-4 py-2.5 h-auto rounded-lg text-sm"
              />
            </div>
          </div>

          {/* Grid: sidebar + content */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-8">
            {/* Sidebar */}
            <aside className="lg:col-span-4 space-y-4">
              <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5">
                <h2 className="text-white font-semibold mb-3">À propos</h2>
                {shop.description ? <p className="text-[#B0B0B0] text-sm leading-relaxed">{shop.description}</p> : <p className="text-[#666] text-sm italic">Aucune description pour le moment.</p>}
                <div className="mt-4 space-y-2.5 text-sm">
                  {shop.address && <div className="flex items-start gap-2.5 text-[#B0B0B0]"><MapPin className="w-4 h-4 text-[#FF6B35] mt-0.5 shrink-0" />{shop.address}</div>}
                  {shop.phone && <div className="flex items-center gap-2.5 text-[#B0B0B0]"><span className="w-4 text-center text-[#FF6B35]">☎</span>{shop.phone}</div>}
                </div>
                {(shop.instagram || shop.facebook || shop.website || shop.discord) && (
                  <div className="flex items-center gap-2.5 mt-4" data-testid="shop-socials">
                    {shop.website && <a href={shop.website.startsWith('http') ? shop.website : `https://${shop.website}`} target="_blank" rel="noreferrer" title="Site web" className="w-12 h-12 rounded-lg bg-[#252525] hover:bg-[#4ECDC4]/20 flex items-center justify-center text-[#4ECDC4] transition-colors" data-testid="shop-social-website"><Globe className="w-6 h-6" /></a>}
                    {shop.instagram && <a href={shop.instagram.startsWith('http') ? shop.instagram : `https://instagram.com/${shop.instagram.replace('@', '')}`} target="_blank" rel="noreferrer" title="Instagram" className="w-12 h-12 rounded-lg bg-[#252525] hover:bg-[#E1306C]/20 flex items-center justify-center text-white hover:text-[#E1306C] transition-colors" data-testid="shop-social-instagram"><Instagram className="w-6 h-6" /></a>}
                    {shop.facebook && <a href={shop.facebook.startsWith('http') ? shop.facebook : `https://facebook.com/${shop.facebook}`} target="_blank" rel="noreferrer" title="Facebook" className="w-12 h-12 rounded-lg bg-[#252525] hover:bg-[#1877F2]/20 flex items-center justify-center text-white hover:text-[#1877F2] transition-colors" data-testid="shop-social-facebook"><Facebook className="w-6 h-6" /></a>}
                    {shop.discord && <a href={shop.discord.startsWith('http') ? shop.discord : `https://discord.gg/${shop.discord}`} target="_blank" rel="noreferrer" title="Discord" className="w-12 h-12 rounded-lg bg-[#252525] hover:bg-[#5865F2]/20 flex items-center justify-center text-white hover:text-[#5865F2] transition-colors" data-testid="shop-social-discord"><MessageCircle className="w-6 h-6" /></a>}
                  </div>
                )}
              </div>

              {openDays.length > 0 && (
                <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5">
                  <h2 className="text-white font-semibold mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-[#4ECDC4]" />Horaires</h2>
                  <div className="space-y-1.5 text-sm">
                    {openDays.map(([k, label, hours]) => (
                      <div key={k} className="flex items-center justify-between text-[#B0B0B0]"><span>{label}</span><span className="text-white">{hours}</span></div>
                    ))}
                  </div>
                </div>
              )}

              {shop.free_play_allowed && (
                <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5" data-testid="shop-free-play">
                  <h2 className="text-white font-semibold mb-3 flex items-center gap-2"><Dices className="w-4 h-4 text-[#7B68EE]" />Jeu libre</h2>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#7B68EE]/15 text-[#7B68EE] mb-2"><Check className="w-3.5 h-3.5" />Jeu libre autorisé</span>
                  {shop.free_play_conditions && <p className="text-[#B0B0B0] text-sm leading-relaxed whitespace-pre-line mt-1">{shop.free_play_conditions}</p>}
                </div>
              )}
            </aside>

            {/* Content: tabs + tournaments */}
            <section className="lg:col-span-8">
              <div className="flex items-center gap-6 border-b border-[#333] mb-5">
                <button onClick={() => setTab('upcoming')} className={`pb-3 font-semibold text-sm transition-colors relative ${tab === 'upcoming' ? 'text-white' : 'text-[#666] hover:text-[#B0B0B0]'}`} data-testid="tab-upcoming">
                  Tournois à venir {shop.upcoming_count > 0 && <span className="ml-1 text-[#FF6B35]">{shop.upcoming_count}</span>}
                  {tab === 'upcoming' && <span className="absolute bottom-0 inset-x-0 h-0.5 bg-[#FF6B35]" />}
                </button>
                <button onClick={() => setTab('past')} className={`pb-3 font-semibold text-sm transition-colors relative ${tab === 'past' ? 'text-white' : 'text-[#666] hover:text-[#B0B0B0]'}`} data-testid="tab-past">
                  Tournois passés {shop.past_count > 0 && <span className="ml-1 text-[#666]">{shop.past_count}</span>}
                  {tab === 'past' && <span className="absolute bottom-0 inset-x-0 h-0.5 bg-[#FF6B35]" />}
                </button>
              </div>

              {tLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#FF6B35] animate-spin" /></div>
              ) : tournaments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 bg-[#1a1a1a] border border-dashed border-[#333] rounded-xl text-center" data-testid="shop-empty-state">
                  <Trophy className="w-12 h-12 text-[#333] mb-3" />
                  <p className="text-[#B0B0B0] font-medium">{tab === 'upcoming' ? 'Aucun tournoi à venir' : 'Aucun tournoi passé'}</p>
                  <p className="text-[#666] text-sm mt-1">{tab === 'upcoming' ? 'Revenez bientôt pour découvrir les prochains événements.' : "Cette boutique n'a pas encore d'historique."}</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="shop-tournaments-list">
                  {tournaments.map((t, i) => <TournamentCard key={t.id} t={t} index={i} />)}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>

      {/* Contact dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Contacter {shop.name}</DialogTitle>
            <DialogDescription className="text-[#888]">Coordonnées renseignées par l'établissement</DialogDescription>
          </DialogHeader>
          {openStatus !== null && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${openStatus ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`} data-testid="contact-open-status">
              <span className={`w-2 h-2 rounded-full ${openStatus ? 'bg-green-400' : 'bg-red-400'}`} />
              {openStatus ? 'Ouvert actuellement' : 'Fermé actuellement'}
            </div>
          )}
          <div className="space-y-2" data-testid="shop-contact-list">
            {shop.email && (
              <a href={`mailto:${shop.email}?subject=${encodeURIComponent(`Contact via Arcanapp – ${shop.name}`)}`} className="flex items-center gap-3 px-3 py-3 rounded-lg bg-[#252525] hover:bg-[#2c2c2c] transition-colors" data-testid="contact-email">
                <span className="w-9 h-9 rounded-lg bg-[#FF6B35]/15 flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-[#FF6B35]" /></span>
                <div className="min-w-0"><p className="text-[#888] text-xs">Email</p><p className="text-white text-sm truncate">{shop.email}</p></div>
              </a>
            )}
            {shop.phone && (
              <a href={`tel:${shop.phone.replace(/\s+/g, '')}`} className="flex items-center gap-3 px-3 py-3 rounded-lg bg-[#252525] hover:bg-[#2c2c2c] transition-colors" data-testid="contact-phone">
                <span className="w-9 h-9 rounded-lg bg-[#4ECDC4]/15 flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-[#4ECDC4]" /></span>
                <div className="min-w-0"><p className="text-[#888] text-xs">Téléphone</p><p className="text-white text-sm truncate">{shop.phone}</p></div>
              </a>
            )}
            {shop.whatsapp && (
              <a href={shop.whatsapp.startsWith('http') ? shop.whatsapp : `https://wa.me/${shop.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-3 rounded-lg bg-[#252525] hover:bg-[#2c2c2c] transition-colors" data-testid="contact-whatsapp">
                <span className="w-9 h-9 rounded-lg bg-[#25D366]/15 flex items-center justify-center shrink-0"><MessageSquare className="w-5 h-5 text-[#25D366]" /></span>
                <div className="min-w-0"><p className="text-[#888] text-xs">WhatsApp</p><p className="text-white text-sm truncate">{shop.whatsapp}</p></div>
              </a>
            )}
            {shop.discord && (
              <a href={shop.discord.startsWith('http') ? shop.discord : `https://discord.gg/${shop.discord}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-3 rounded-lg bg-[#252525] hover:bg-[#2c2c2c] transition-colors" data-testid="contact-discord">
                <span className="w-9 h-9 rounded-lg bg-[#5865F2]/15 flex items-center justify-center shrink-0"><MessageCircle className="w-5 h-5 text-[#5865F2]" /></span>
                <div className="min-w-0"><p className="text-[#888] text-xs">Discord</p><p className="text-white text-sm truncate">{shop.discord}</p></div>
              </a>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
